import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import { commandScore, useGetAllFoodsQuery } from '@durma-soft/gros-sdk';
import { ShortRestaurantFood } from '@durma-soft/gros-sdk/dist/types/restaurants';

import { Spinner } from '@/components/shared/spinner';
import { Button } from '@/components/shared/shadcn-ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/shadcn-ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shared/shadcn-ui/command';

import { cn } from '@/utils/helpers';

interface FoodComboboxProps {
  restaurantId: number;
  selectedFoods: ShortRestaurantFood[];
  onUpdateFoods: (foods: ShortRestaurantFood[]) => unknown;
}

export const FoodsCombobox = ({
  restaurantId,
  selectedFoods,
  onUpdateFoods,
}: FoodComboboxProps) => {
  const { t } = useTranslation();
  const [, startTransition] = useTransition();
  const [open, setOpen] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const commandListRef = useRef<HTMLDivElement>(null);

  const { data: foods, isLoading } = useGetAllFoodsQuery(restaurantId);

  const sortedData = useMemo(() => {
    return (foods ? [...foods] : [])
      .filter((element) => commandScore(element.name, searchInput) > 0)
      .sort((a, b) => {
        return (
          commandScore(b.name, searchInput) - commandScore(a.name, searchInput)
        );
      });
  }, [foods, searchInput]);

  useLayoutEffect(() => {
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0;
    }
  }, [sortedData]);

  if (isLoading) return <Spinner />;

  const toggleFood = (food: ShortRestaurantFood) => {
    if (selectedFoods.some((i) => i.id === food.id)) {
      onUpdateFoods(selectedFoods.filter((i) => i.id !== food.id));
    } else {
      onUpdateFoods([...selectedFoods, { ...food }]);
    }
  };

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          {selectedFoods.length > 0
            ? `${selectedFoods.length} ${t('common.chosen')}`
            : t('menu.choose-foods')}
          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={searchInput}
            onValueChange={(newValue) => {
              startTransition(() => {
                setSearchInput(newValue);
              });
            }}
            placeholder={t('common.search-placeholder')}
          />
          <CommandEmpty>{t('menu.food-not-found')}</CommandEmpty>
          <CommandGroup>
            <CommandList ref={commandListRef}>
              {foods &&
                sortedData.map((food) => (
                  <CommandItem
                    onSelect={() => {
                      toggleFood(food);
                    }}
                    key={food.id}
                    value={`${food.name}_id:${food.id}`}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedFoods.some((i) => i.id === food.id)
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                    {food.name}
                  </CommandItem>
                ))}
            </CommandList>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
